import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

import { cn } from "@core/utils/domUtil";
import { buttonVariants } from "@core/components/ui/Button";

export type AlertDialogPropsType = {
  /* AlertDialogPropsType: 초기 열림 상태 */
  defaultOpen?: boolean;
  /* AlertDialogPropsType: 열림 상태 */
  open?: boolean;
  /* AlertDialogPropsType: 열림 상태 변경 시 호출 */
  onOpenChange?: (open: boolean) => void;
} & React.ComponentProps<typeof AlertDialogPrimitive.Root>;

function AlertDialog({ ...props }: AlertDialogPropsType) {
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />;
}

function AlertDialogTrigger({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
  return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />;
}

function AlertDialogPortal({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />;
}

function AlertDialogOverlay({ className, ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  return <AlertDialogPrimitive.Overlay data-slot="alert-dialog-overlay" className={cn("data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-55 bg-black/80", className)} {...props} />;
}

interface AlertDialogContentPropsType extends React.ComponentProps<typeof AlertDialogPrimitive.Content> {
  /* AlertDialogContentPropsType: 앨럿 이름 */
  name?: string;
}

function AlertDialogContent({ className, name, ...props }: AlertDialogContentPropsType) {
  const preventEscapeKey = React.useCallback((e: KeyboardEvent) => {
    e.preventDefault();
  }, []);

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content data-slot="alert-dialog-content" data-name={name} className={cn("data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-55 w-fit max-w-[90%] min-w-[300px] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg bg-white p-[20px] shadow-[0px_4px_6px_rgba(0,0,0,0.09)] duration-200", "lg:max-w-[400px] lg:min-w-[320px] lg:pt-[25px]", className)} {...props} onEscapeKeyDown={preventEscapeKey} />
    </AlertDialogPortal>
  );
}

function AlertDialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="alert-dialog-header" className={cn("flex flex-col gap-2 text-center sm:text-left", className)} {...props} />;
}

function AlertDialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="alert-dialog-footer" className={cn("mt-[30px] flex flex-row justify-center gap-[10px]", className)} {...props} />;
}

function AlertDialogTitle({ className, ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return <AlertDialogPrimitive.Title data-slot="alert-dialog-title" className={cn("text-lg font-semibold", className)} {...props} />;
}

function AlertDialogDescription({ className, ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return <AlertDialogPrimitive.Description data-slot="alert-dialog-description" className={cn("text-md pt-[16px] text-center lg:text-lg", className)} {...props} />;
}

function AlertDialogAction({ className, ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Action>) {
  return <AlertDialogPrimitive.Action className={cn(buttonVariants({ variant: "bgPoint600", size: "lg" }), "cursor-pointer rounded-lg", className)} {...props} />;
}

// 컨펌 말고, 얼럿에서 사용할 컴포넌트
// 이거 필요한 이유는 얼럿, 즉 확인 버튼 하나만 있는곳에서 엔터쳐서 닫으려면 AlertDialogPrimitive.Cancel 컴포넌트로 감싸져 있어야 해서
// AlertDialogPrimitive.Action 은 사용자가 뭔가 행동하는거기 때문에 무심코 누른 엔터로 동작 안되게 되어 있음
function AlertDialogCancelForAlert({ className, ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Action>) {
  return <AlertDialogPrimitive.Cancel className={cn(buttonVariants({ variant: "bgPoint600", size: "lg" }), "cursor-pointer rounded-lg", className)} {...props} />;
}

function AlertDialogCancel({ className, ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Cancel>) {
  return <AlertDialogPrimitive.Cancel className={cn(buttonVariants({ variant: "borderBgGray50", size: "lg" }), "cursor-pointer rounded-lg", className)} {...props} />;
}

export { AlertDialog, AlertDialogPortal, AlertDialogOverlay, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancelForAlert, AlertDialogCancel };
